import React, { useEffect, useState } from "react";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function PaymentSuccess() {
  const { lang, refreshUser } = useApp();
  const L = (it, en) => (lang === "en" ? en : it);
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [state, setState] = useState("checking"); // checking | paid | timeout
  const sessionId = params.get("session_id");

  useEffect(() => {
    if (!sessionId) { setState("timeout"); return; }
    let tries = 0;
    let stop = false;
    const poll = async () => {
      if (stop) return;
      tries += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState("paid");
          if (refreshUser) await refreshUser();
          return;
        }
      } catch { /* keep polling */ }
      if (tries >= 12) { setState("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
  }, [sessionId, refreshUser]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6" data-testid="payment-success-page">
      <div className="max-w-md w-full text-center rounded-2xl border border-border p-10">
        {state === "checking" && (
          <>
            <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4 text-primary" />
            <h1 className="text-xl font-bold mb-2">{L("Confermo il pagamento…", "Confirming payment…")}</h1>
            <p className="text-sm text-muted-foreground">{L("Attendi qualche istante.", "This takes a moment.")}</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="w-12 h-12 mx-auto mb-4 text-green-600" />
            <h1 className="text-xl font-bold mb-2" data-testid="payment-success-title">{L("Pagamento riuscito!", "Payment successful!")}</h1>
            <p className="text-sm text-muted-foreground mb-6">{L("Il tuo abbonamento è attivo. Buon lavoro con Revenue AI.", "Your subscription is active. Enjoy Revenue AI.")}</p>
            <Button className="w-full" onClick={() => nav("/dashboard")} data-testid="go-dashboard-btn">{L("Vai alla dashboard", "Go to dashboard")}</Button>
          </>
        )}
        {state === "timeout" && (
          <>
            <XCircle className="w-12 h-12 mx-auto mb-4 text-amber-600" />
            <h1 className="text-xl font-bold mb-2">{L("Verifica in corso", "Still verifying")}</h1>
            <p className="text-sm text-muted-foreground mb-6">{L("Il pagamento potrebbe richiedere qualche minuto. Controlla lo stato nella pagina Abbonamento.", "Payment may take a few minutes. Check the status on the Subscription page.")}</p>
            <Button className="w-full" onClick={() => nav("/subscription")} data-testid="go-subscription-btn">{L("Vai all'abbonamento", "Go to subscription")}</Button>
          </>
        )}
      </div>
    </div>
  );
}
